// OWNED BY the calls implementation agent (see ../CallsTab.tsx ownership note).
//
// One call in the s24 timeline: direction icon circle (in=green / out=blue /
// missed=red) with a connector line, title (direction · duration) + AI chip +
// relative time, the AI summary quote block, and actions — play pill that
// expands the slim audio player + full-transcript link, "משימה מהשיחה" (opens
// the app's calendar task editor prefilled from the call — the standard write
// path, showSingleEventEditorModalByType), and "חייג חזרה" (tel:) on missed
// calls. Transcript/summary is lazy-loaded per rendered row from the nested
// enrichment/transcriptAndSummary doc through the session cache.

import { useState } from "react";
import { Box, Stack, Button, Collapse, Skeleton, Typography } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import {
  Add,
  Call,
  PlayArrow,
  ExpandLess,
  Description,
  PhoneMissed,
  PhoneCallback,
  PhoneForwarded
} from "@material-ui/icons";
import Label from "components/Label";
import useCalendar from "hooks/useCalendar";
import { parseContactsToAttendees } from "utils/calendar/helper";
import { useSessionCacheValue } from "../../data/sessionCache";
import { CallAudioPlayer } from "./CallAudioPlayer";
import { CallTranscriptDialog } from "./CallTranscriptDialog";
import {
  classifyCall,
  isMissedCall,
  formatCallWhen,
  callTranscriptKey,
  contactDisplayName,
  formatCallDuration,
  fetchCallTranscript,
  callDurationSeconds,
  missedCallResultText,
  TRANSCRIPT_MAX_AGE_MS,
  getCallAudioUrlCandidates
} from "./callsData";
import type {
  CallSummary,
  RealEstateContact,
  CallTranscriptAndSummary
} from "../../../../../../@types/sharedSchema";

type ContactCallRowProps = {
  contact: RealEstateContact;
  call: CallSummary;
  isLast: boolean;
  /** "<virtualNumber>|<callId>" of the row whose player is currently playing. */
  playingCallId: string | null;
  onPlayingCallIdChange: (id: string | null) => void;
};

export function ContactCallRow({
  contact,
  call,
  isLast,
  playingCallId,
  onPlayingCallIdChange
}: ContactCallRowProps): JSX.Element {
  const theme = useTheme();
  const { showSingleEventEditorModalByType } = useCalendar();

  const [expanded, setExpanded] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  const missed = isMissedCall(call);
  const answered = !missed;

  // Transcription exists only for answered calls (the pipeline skips
  // unanswered/<30s) — skip the Firestore read entirely for missed rows.
  const { data: transcript, loading: loadingTranscript } = useSessionCacheValue<
    CallTranscriptAndSummary | null
  >(
    callTranscriptKey(contact.id, call),
    () => (answered ? fetchCallTranscript(call) : Promise.resolve(null)),
    { maxAgeMs: TRANSCRIPT_MAX_AGE_MS }
  );

  const kind = classifyCall(call);

  const kindPalette =
    kind === "missed"
      ? theme.palette.error
      : kind === "outgoing"
      ? theme.palette.secondary
      : theme.palette.primary;

  const kindIcon =
    kind === "missed" ? (
      <PhoneMissed fontSize="small" />
    ) : kind === "outgoing" ? (
      <PhoneForwarded fontSize="small" />
    ) : (
      <PhoneCallback fontSize="small" />
    );

  const directionLabel = call.callDirection === "outbound" ? "שיחה יוצאת" : "שיחה נכנסת";
  const durationLabel = formatCallDuration(callDurationSeconds(call));
  const title = missed ? "שיחה שלא נענתה" : `${directionLabel} · ${durationLabel}`;
  const whenLabel = formatCallWhen(call);

  const hasAi = Boolean(transcript?.AI);
  const summaryText = transcript?.AI?.callSummary ?? transcript?.summary ?? "";
  const splitTranscription = transcript?.splitTranscription ?? [];
  const hasFullTranscript = splitTranscription.length > 0;

  const hasRecording = answered && getCallAudioUrlCandidates(call).length > 0;
  const rowPlayerId = `${call.virtualNumber}|${call.id}`;

  const handleToggleExpanded = (): void => {
    if (expanded && playingCallId === rowPlayerId) onPlayingCallIdChange(null);
    setExpanded(!expanded);
  };

  const handleCreateTaskFromCall = (): void => {
    showSingleEventEditorModalByType("task", {
      attendees: parseContactsToAttendees([contact]),
      title: `מעקב שיחה — ${contactDisplayName(contact)}`,
      description: [
        `משימה שנוצרה מ${missed ? "שיחה שלא נענתה" : directionLabel} (${whenLabel})`,
        summaryText ? `סיכום השיחה: ${summaryText}` : ""
      ]
        .filter(Boolean)
        .join("\n")
    });
  };

  return (
    <Box
      sx={{
        position: "relative",
        pb: isLast ? 0 : 3,
        ...(isLast
          ? {}
          : {
              "&::before": {
                content: '""',
                position: "absolute",
                top: 44,
                bottom: 8,
                insetInlineStart: 19,
                width: "2px",
                bgcolor: "divider"
              }
            })
      }}
    >
      <Stack direction="row" gap={1.5} alignItems="flex-start">
        {/* direction icon circle */}
        <Box
          sx={{
            width: 40,
            height: 40,
            zIndex: 1,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            color: kindPalette.dark,
            bgcolor: alpha(kindPalette.main, 0.12)
          }}
        >
          {kindIcon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* title line */}
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2">{title}</Typography>
            {hasAi && <Label color="info">AI</Label>}
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", marginInlineStart: "auto", whiteSpace: "nowrap" }}
            >
              {whenLabel}
            </Typography>
          </Stack>

          {missed && (
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25 }}>
              {`${directionLabel} — ${missedCallResultText(call.result)}`}
            </Typography>
          )}

          {/* AI summary quote block */}
          {summaryText ? (
            <Typography
              variant="body2"
              dir="auto"
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.neutral",
                whiteSpace: "pre-line",
                overflowWrap: "break-word"
              }}
            >
              {summaryText}
            </Typography>
          ) : (
            answered &&
            loadingTranscript &&
            transcript === undefined && (
              <Skeleton variant="text" width="60%" sx={{ mt: 1 }} />
            )
          )}

          {/* actions */}
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 1 }}>
            {hasRecording && (
              <Button
                size="small"
                color="primary"
                startIcon={expanded ? <ExpandLess /> : <PlayArrow />}
                onClick={handleToggleExpanded}
                sx={{
                  px: 1.5,
                  borderRadius: 999,
                  color: "primary.dark",
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.24) }
                }}
              >
                {expanded ? "סגירת הנגן" : hasFullTranscript ? "הקלטה + תמלול מלא" : "הקלטה"}
              </Button>
            )}
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              startIcon={<Add />}
              onClick={handleCreateTaskFromCall}
            >
              משימה מהשיחה
            </Button>
            {missed && (
              <Button
                size="small"
                color="inherit"
                variant="outlined"
                startIcon={<Call />}
                href={`tel:${contact.id}`}
              >
                חייג חזרה
              </Button>
            )}
          </Stack>

          {/* expanded: audio player + full transcript */}
          {hasRecording && (
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Stack gap={1} sx={{ mt: 1.5 }} alignItems="flex-start">
                <CallAudioPlayer
                  call={call}
                  isActivePlayer={playingCallId === rowPlayerId}
                  onBecameActive={() => onPlayingCallIdChange(rowPlayerId)}
                />
                {hasFullTranscript && (
                  <Button
                    size="small"
                    startIcon={<Description />}
                    onClick={() => setTranscriptOpen(true)}
                  >
                    תמלול מלא
                  </Button>
                )}
              </Stack>
            </Collapse>
          )}
        </Box>
      </Stack>

      {hasFullTranscript && (
        <CallTranscriptDialog
          open={transcriptOpen}
          transcription={splitTranscription}
          onClose={() => setTranscriptOpen(false)}
        />
      )}
    </Box>
  );
}
